import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Fuel, Weight, Layers, Circle, Truck } from "lucide-react";

export default function ForkliftCard({ forklift, onClick }) {
  const { t, lang } = useLanguage();
  const desc = lang === "en" ? forklift.description_en : forklift.description_fr;

  const engineLabel = { propane: "Propane", diesel: "Diesel", electric: lang === "en" ? "Electric" : "Electrique" };
  const tireLabel = { pneumatic: lang === "en" ? "Pneumatic" : "Pneumatique", cushion: lang === "en" ? "Cushion" : "Coussin" };

  const imgSrc = forklift.image_url
    ? (forklift.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${forklift.image_url}` : forklift.image_url)
    : null;

  const content = (
    <>
      <div className="aspect-[4/3] bg-gray-100 overflow-hidden relative">
        {imgSrc ? (
          <img src={imgSrc} alt={`${forklift.brand} ${forklift.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Truck className="w-16 h-16 text-gray-300" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className={forklift.condition === "new"
            ? "bg-yellow-400 text-gray-900 border-none hover:bg-yellow-400"
            : "bg-gray-100 text-gray-700 border-none hover:bg-gray-100"}>
            {forklift.condition === "new" ? (lang === "en" ? "New" : "Neuf") : (lang === "en" ? "Used" : "Usage")}
          </Badge>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-heading text-lg font-semibold text-gray-900">{forklift.brand} {forklift.model}</h3>
            {forklift.year && <p className="text-xs text-gray-500">{forklift.year}</p>}
          </div>
          <p className="font-mono text-lg font-bold text-gray-900">${forklift.price?.toLocaleString()}</p>
        </div>
        {desc && <p className="text-sm text-gray-500 mb-3 line-clamp-2">{desc}</p>}
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
          <div className="flex items-center gap-1.5">
            <Weight className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-mono">{forklift.capacity_lbs?.toLocaleString()} {t.inventory.lbs}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Fuel className="w-3.5 h-3.5 text-gray-400" />
            <span>{engineLabel[forklift.engine_type] || forklift.engine_type}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-gray-400" />
            <span>{forklift.mast_stages} {t.forklift_card.stages}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Circle className="w-3.5 h-3.5 text-gray-400" />
            <span>{tireLabel[forklift.tire_type] || forklift.tire_type}</span>
          </div>
        </div>
        {forklift.hours_used != null && (
          <p className="mt-2 text-xs text-gray-400 font-mono">{forklift.hours_used?.toLocaleString()} {t.inventory.hours}</p>
        )}
        {!onClick && (
          <Link
            to={`/inventory/${forklift.id}`}
            data-testid={`view-detail-${forklift.id}`}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-4 py-2 rounded-sm text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {t.inventory.view_details}
          </Link>
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <div data-testid={`forklift-card-${forklift.id}`} onClick={onClick}
        className="bg-white border border-gray-200 rounded-sm overflow-hidden group cursor-pointer hover:-translate-y-0.5 transition-all duration-200">
        {content}
      </div>
    );
  }

  return (
    <Link to={`/inventory/${forklift.id}`} data-testid={`forklift-card-${forklift.id}`}
      className="bg-white border border-gray-200 rounded-sm overflow-hidden group cursor-pointer hover:-translate-y-0.5 transition-all duration-200 block">
      {content}
    </Link>
  );
}
